import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { security, type SecurityEvent } from "@/api/client";
import { Chip, type ChipTone } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { ErrorNote } from "@/components/ErrorNote";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { ago } from "@/lib/format";

/** The security audit log: what was minted, for what, by whom, when.
 *
 *  Dattatray: *"Secutiy audit log : What wasminted for which asset/Component
 *  /part / I should also be able to add filters and see what exactly happned
 *  for a specifc device and when!"* and *"Dont show the secret that was minted
 *  on UI / Just show log on what was minted for by who and when and for what
 *  purpose etc..!"*
 *
 *  NO SECRET REACHES THIS FILE, and not because the page declines to render
 *  one. The server's audit view names its columns and the key material is not
 *  among them, so there is no field here to leak — `tests/security` compiles
 *  the actual SQL and asserts the projection. A page that received the key and
 *  chose not to show it would be one `console.log` from publishing it.
 *
 *  WHAT A SERIAL IS. The certificate serial IS shown, and it is not a secret:
 *  it is printed in the certificate, which is the public half. It is also the
 *  only handle an operator has for "revoke this exact certificate".
 *
 *  ONE TIMELINE, NOT TWO TABLES. A board's certificate and its BLE key are two
 *  answers to "what happened to this device", and the cases that matter are
 *  the ones where their ORDER matters.
 */

const EVENT_TONE: Record<string, ChipTone> = {
  minted: "ok", revoked: "bad", superseded: "warn",
};

/** What each event means, in the console's own words. Hover text, because the
 *  difference between "revoked" and "superseded" is exactly the thing an
 *  operator reading an incident needs and would otherwise guess at. */
const EVENT_MEANS: Record<string, string> = {
  minted: "Orbit issued this secret and handed it to the EOL tool once",
  revoked: "deliberately killed. The device stops being accepted on its next connect",
  superseded: "replaced by a newer key. Rotation closes the old row rather than overwriting it",
};

export function AuditLogPage() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [event, setEvent] = useState("");

  // NO POLL. An audit log of EOL mints changes when somebody runs a factory
  // tool, not continuously, and the UI performance laws budget poll cadence
  // for live views. Refetching this every few seconds would spend the budget
  // on a page that is almost always identical to its last render.
  const log = useQuery({
    queryKey: ["security", "audit", q],
    queryFn: () => security.audit(q || undefined),
  });
  const status = useQuery({
    queryKey: ["security", "status"],
    queryFn: security.status,
  });

  // CLIENT-SIDE, deliberately, and only these two. The server already filters
  // by identifier (the expensive, index-backed one); kind and event are a
  // handful of values over an already-fetched page, so a round trip per
  // dropdown change would be latency for nothing.
  const rows = useMemo(() => (log.data ?? []).filter(
    (e) => (!kind || e.subject_kind === kind) && (!event || e.event === event)),
    [log.data, kind, event]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Security"
        title="Audit log"
        desc="Every secret Orbit has minted or revoked, and who asked for it.
              The secrets themselves are never shown here, and the device
              private keys are never stored at all."
      />

      {/* THE HONEST STATE OF THIS BOX. Without it an empty table reads as
          "nothing issued" when it may mean "this box cannot issue". */}
      {status.data && !status.data.provisioning_enabled && (
        <div className="panel panel-pad" style={{ marginBottom: 12,
              borderLeft: "3px solid var(--warn)" }}>
          <b style={{ color: "var(--warn)" }}>Provisioning is off on this box</b>
          <div style={{ fontSize: 12.5, marginTop: 4, color: "var(--t-mute)" }}>
            Nothing can be minted here. The log below is the real history, and
            it stays empty until the EOL provisioning flow is switched on after
            firmware integration testing.
          </div>
        </div>
      )}

      <div className="panel panel-pad" style={{ marginBottom: 12, display: "flex",
            gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {/* ONE SEARCH BOX FOR EVERY IDENTIFIER. Somebody holding a board knows
            its serial, somebody on a ticket knows the asset, somebody in a
            broker log knows the IMEI. Making them choose the field first is
            making them translate. */}
        <SearchInput mono value={q} style={{ minWidth: 280 }}
          placeholder="IMEI, board serial, BLE MAC, asset or cert serial"
          onChange={(e) => setQ(e.target.value)} />
        <Select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Device and part</option>
          <option value="device">Device (modem)</option>
          <option value="part">Part (board)</option>
        </Select>
        <Select value={event} onChange={(e) => setEvent(e.target.value)}>
          <option value="">Every event</option>
          <option value="minted">Minted</option>
          <option value="revoked">Revoked</option>
          <option value="superseded">Superseded</option>
        </Select>
        <span className="cap" style={{ marginLeft: "auto" }}>
          {rows.length} event{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {log.isError && <ErrorNote error={log.error} />}

      <DataTable rows={rows} cols={COLS}
        rowKey={(e) => `${e.ts}-${e.subject}-${e.event}`}
        maxHeight={620} pageSize={100}
        empty={<div className="panel panel-pad cap">
          Nothing has been minted yet.
        </div>} />
    </div>
  );
}

/** Module-const so DataTable's memoized rows do their job (UI law: stable
 *  keys + memoized rows). Rebuilding this array each render remounts every
 *  row on every keystroke in the search box. */
const COLS: DataCol<SecurityEvent>[] = [
  { key: "when", label: "When", width: 130,
    title: (e) => e.ts ?? "",
    render: (e) => (e.ts ? `${ago(e.ts)} ago` : "—") },
  { key: "event", label: "Event", width: 120,
    title: (e) => EVENT_MEANS[e.event] ?? e.event,
    render: (e) => <Chip tone={EVENT_TONE[e.event] ?? "mute"}>
      {e.event.toUpperCase()}
    </Chip> },
  { key: "what", label: "What", width: 150, render: (e) => e.what },
  { key: "subject", label: "For", width: 170, mono: true,
    title: (e) => `${e.subject_kind}: ${e.subject}`,
    render: (e) => e.subject },
  { key: "asset", label: "Asset", width: 140, mono: true,
    // NOT "unknown": a mint can legitimately predate the part being fitted to
    // anything, which is the normal case at end of line.
    render: (e) => e.asset_id ?? "—" },
  { key: "actor", label: "By", width: 150,
    render: (e) => e.actor ?? "—" },
  { key: "detail", label: "Detail", width: 230, mono: true,
    title: (e) => e.detail,
    render: (e) => e.detail || "—" },
  { key: "note", label: "Purpose", width: 220,
    title: (e) => e.note ?? "",
    render: (e) => e.note ?? "—" },
];
